import { useState, type ReactNode } from 'react';
import { Menu, MenuItem } from '@strivelabs/ui-core/v2';
import { X } from 'lucide-react';
import { cn } from '@strivelabs/ui-core/lib/utils';

/** One choice a property row offers. */
export interface PickerOption {
  value: string;
  label: string;
  node: ReactNode;
}

/**
 * The resting shape of an editable value.
 *
 * Nothing about it says "field" until the pointer is over it, which is what
 * lets seven properties sit in a column without reading as a form.
 *
 * One box for every property: a fixed 30px so a row of label chips hovers at
 * the same height as a name or a date, and 9px of padding that {@link
 * INLINE_FIELD_BLEED} pulls back into the gutter so the value's first glyph
 * sits on the column edge.
 */
export const INLINE_FIELD_BOX = 'inline-flex h-[30px] max-w-full items-center gap-[7px] whitespace-nowrap rounded-md px-[9px] hover:bg-[#F6F5F1]';

/**
 * The bleed, kept apart from the box because it has to sit on whatever the
 * box's width is measured against. On a button placed straight in the row
 * that is the button. Behind a Popover it is the Popover's inline wrapper,
 * which shrinks to its content: a bleed on the button shrank the wrapper by
 * the same 18px, the button's `max-w-full` then clamped it to that, and the
 * hover fill stopped short of the value's right edge.
 */
export const INLINE_FIELD_BLEED = '-mx-[9px]';

/**
 * Used where the button is itself the row's child (the value menus), whose
 * width is definite: the bleed goes on the button, and so does the room for it.
 */
export const INLINE_FIELD = cn(INLINE_FIELD_BOX, INLINE_FIELD_BLEED, 'max-w-[calc(100%_+_18px)]');

/**
 * The Popover wrapper's classes for a field it hosts: the bleed, and room for
 * it so a value that fills the row still ends on the row's edge.
 */
export const INLINE_FIELD_POPOVER_TRIGGER = `${INLINE_FIELD_BLEED} min-w-0 max-w-[calc(100%_+_18px)]`;

interface TaskInlinePickerProps {
  label: string;
  /** What the value reads as when the picker is closed. */
  children: ReactNode;
  options: PickerOption[];
  selected: string | null;
  /**
   * Names the × beside the current value that empties the property. Offered
   * only where the property may hold nobody, which the assignee may not.
   */
  clearLabel?: string;
  disabled?: boolean;
  onSelect: (value: string | null) => void;
}

/**
 * A property whose value is chosen from a list.
 *
 * Status, assignee and reviewer are the same interaction wearing different
 * values, so they are the same component: one dropdown that opens over the
 * value it is changing, rather than three that each drift.
 */
export const TaskInlinePicker = ({
  label, children, options, selected, clearLabel, disabled = false, onSelect,
}: TaskInlinePickerProps) => {
  const [ open, setOpen ] = useState(false);
  const current = options.find((option) => option.value === selected);

  return (
    <Menu
      aria-label={label}
      disabled={disabled}
      open={open}
      onOpenChange={setOpen}
      contentClassName="min-w-[210px] max-h-[300px]"
      trigger={
        <button
          type="button"
          aria-label={label}
          disabled={disabled}
          className={cn(INLINE_FIELD, disabled && 'pointer-events-none')}
        >
          {children}
        </button>
      }
    >
      {/* What the property holds now, pinned over the list as the labels
          editor pins its tags: the reader sees the value they are changing
          without finding it in the list, and a property that may be emptied
          is emptied from here. */}
      {current ? (
        <div
          data-testid="picker-current"
          className="sticky -top-1 z-10 -mx-1 -mt-1 mb-1 flex items-center gap-2 rounded-t-[inherit] border-b border-[rgba(0,0,0,0.06)] bg-[#FBFAF7] px-[13px] py-2 text-[13.5px]"
        >
          <span className="min-w-0 flex-1">{current.node}</span>
          {clearLabel ? (
            <button
              type="button"
              aria-label={clearLabel}
              className="shrink-0 rounded-[3px] text-[#9A9A9A] hover:text-[#181818]"
              onClick={() => {
                onSelect(null);
                setOpen(false);
              }}
            >
              <X className="size-3" aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}

      {options.map((option) => (
        <MenuItem
          key={option.value}
          label={option.label}
          className={cn(
            'gap-2.5 px-[9px] py-[7px] text-[13.5px]',
            option.value === selected && 'bg-[#FBFAF7]',
          )}
          onClick={() => onSelect(option.value)}
        >
          {option.node}
        </MenuItem>
      ))}
    </Menu>
  );
};
